<template>
  <li class="ml-0 pb-2 pl-4 ">
    <!-- If it has children, render as collapsible item -->
    <details
      v-if="node.children?.length"
      :open="isOpen"
      class="group"
    >
      <summary
        class="flex cursor-pointer items-center justify-between gap-2 py-1 text-gray-800 hover:text-primary-600"
        @click.stop.prevent="isOpen = !isOpen"
      >
        <div class="flex items-center gap-2">
          <input
            :id="path"
            ref="checkboxRef"
            type="checkbox"
            :checked="isChecked()"
            class="accent-primary-600"
            @change.stop="handleChange"
            @click.stop
          >
          <label
            :for="path"
            class="cursor-pointer font-bold"
            @click.stop
          >{{ node.value }}</label>
        </div>
        <svg
          class="size-4 text-gray-500 transition-transform duration-200"
          :class="{ 'rotate-90': isOpen }"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </summary>

      <ul class="mt-1  pl-2">
        <CategoryTree
          v-for="child in node.children"
          :key="child.value"
          :node="child"
          :path="`${path}||${child.value}`"
          :selected-paths="selectedPaths"
          :initial-value="initialValue"
          @update:selected-paths="$emit('update:selectedPaths', $event)"
        />
      </ul>
    </details>

    <!-- Leaf node -->
    <div
      v-else
      class="flex items-center gap-2 py-1 text-gray-700 hover:text-primary-600"
    >
      <input
        :id="path"
        ref="checkboxRef"
        type="checkbox"
        :checked="isChecked()"
        class="accent-primary-600"
        @change="handleChange"
      >
      <label
        :for="path"
        class="cursor-pointer"
      >{{ node.value }}</label>
    </div>
  </li>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  path: {
    type: String,
    required: true,
  },
  selectedPaths: {
    type: Set,
    required: true, // Set<string>
  },
  initialValue: {
    type: String,
    default: '', // Comma-separated paths
  },
});

const { initialValue, selectedPaths } = toRefs(props);

const emit = defineEmits(['update:selectedPaths']);

const checkboxRef = ref<HTMLInputElement | null>(null);
const isOpen = ref(false);

const getDescendantPaths = (node: any, basePath: string): string[] => {
  const paths: string[] = [];
  if (!node.children) {
    return paths;
  }

  for (const child of node.children) {
    const childPath = `${basePath}||${child.value}`;
    paths.push(childPath);
    paths.push(...getDescendantPaths(child, childPath));
  }

  return paths;
};

const isChecked = () => {
  const isLeaf = !props.node.children?.length;

  if (props.selectedPaths.has(props.path)) {
    return true;
  }

  if (!isLeaf) {
    // Parent visually checked if any descendant selected
    return [...props.selectedPaths].some((p: any) => p.startsWith(`${props.path}||`));
  }

  return false; // Leaf only checked if directly selected
};

const handleChange = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  const descendantPaths = getDescendantPaths(props.node, props.path);

  const isLeaf = !props.node.children?.length;

  if (checked) {
    if (isLeaf) {
      // Add only leaf node
      props.selectedPaths.add(props.path);
    }
    else {
      // Clicking a parent adds it and removes descendants
      props.selectedPaths.add(props.path);
      for (const path of descendantPaths) {
        props.selectedPaths.delete(path);
      }
    }
  }
  else {
    props.selectedPaths.delete(props.path);
    if (!isLeaf) {
      for (const path of descendantPaths) {
        props.selectedPaths.delete(path);
      }
    }
  }

  emit('update:selectedPaths', new Set(props.selectedPaths));
};

watch(initialValue, () => {
  // Only the root node should initialize the selectedPaths
  if (props.path !== props.node.value || !props.initialValue) {
    return;
  }

  const initialPaths = new Set(
    props.initialValue?.split(',').map(p => p.trim()).filter(Boolean),
  );

  const recursivelySelectInitial = (node: any, currentPath: string) => {
    if (initialPaths.has(currentPath)) {
      props.selectedPaths.add(currentPath);
    }

    if (node.children?.length) {
      for (const child of node.children) {
        const childPath = `${currentPath}||${child.value}`;
        recursivelySelectInitial(child, childPath);
      }
    }
  };

  recursivelySelectInitial(props.node, props.path);
  emit('update:selectedPaths', new Set(props.selectedPaths));
}, { once: true });
</script>
